import { useScrollOffset } from '@graphcommerce/framer-next-pages'
import { dvh } from '@graphcommerce/framer-utils'
import { Box, SxProps, Theme } from '@mui/material'
import { useTransform, useScroll } from 'framer-motion'
import { LayoutProvider } from '@graphcommerce/next-ui/Layout/components/LayoutProvider'
import { extendableComponent } from '@graphcommerce/next-ui/Styles'
import { useFabSize } from '@graphcommerce/next-ui/Theme'

export type LayoutDefaultProps = {
  className?: string
  beforeHeader?: React.ReactNode
  header: React.ReactNode
  footer: React.ReactNode
  menuFab?: React.ReactNode
  cartFab?: React.ReactNode
  children?: React.ReactNode
  noSticky?: boolean
  sx?: SxProps<Theme>
} & OwnerState

type OwnerState = {
  noSticky?: boolean
}
const parts = ['root', 'fabs', 'header', 'children', 'footer'] as const
const { withState } = extendableComponent<OwnerState, 'LayoutDefault', typeof parts>(
  'LayoutDefault',
  parts,
)

export function LayoutDefault(props: LayoutDefaultProps) {
  const {
    children,
    header,
    beforeHeader,
    footer,
    menuFab,
    cartFab,
    noSticky,
    className,
    sx = [],
  } = props

  const { scrollY } = useScroll()
  const scrollYOffset = useTransform(
    [scrollY, useScrollOffset()],
    ([y, offset]: number[]) => y + offset,
  )
  const classes = withState({ noSticky })
  const stickyHeader = true
  const beforeHeaderHeight = beforeHeader ? '70px' : '0px'

  const fabIconSize = useFabSize('responsive')

  return (
    <Box
      className={`${classes.root} ${className ?? ''}`}
      sx={[
        (theme) => ({
          minHeight: dvh(100),
          '@supports (-webkit-touch-callout: none)': {
            minHeight: '-webkit-fill-available',
          },
          display: 'grid',
          gridTemplateRows: (beforeHeader ? `auto auto auto 1fr auto` : `auto auto 1fr auto`),
          gridTemplateColumns: '100%',
          background: theme.palette.background.default,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <LayoutProvider scroll={scrollYOffset}>
        {beforeHeader}
        <Box
          component='header'
          className={`${classes.header}${stickyHeader ? ' sticky-header' : ''}`}
          sx={(theme) => ({
            zIndex: theme.zIndex.appBar - 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: theme.appShell.headerHeightSm,
            pointerEvents: 'none',
            '& > *': {
              pointerEvents: 'all',
            },
            [theme.breakpoints.up('md')]: {
              height: theme.appShell.headerHeightMd,
              padding: `0 ${theme.page.horizontal} 0`,
              top:  (stickyHeader && beforeHeader ? '37px' : '0'),
              display: 'flex',
              justifyContent: 'left',
              width: '100%',
              position: (stickyHeader ? 'sticky' : 'relative'),
              backgroundColor: theme.palette.background.default,
            },
            '&.sticky': {
              [theme.breakpoints.down('md')]: {
                position: 'sticky',
                top: (beforeHeader ? '37px' : '0'),
              },
            },
          })}
        >
          {header}
        </Box>
        {menuFab || cartFab ? (
          <Box
            className={classes.fabs}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              height: 0,
              zIndex: theme.zIndex.appBar + 1,
              [theme.breakpoints.up('sm')]: {
                padding: `0 ${theme.page.horizontal}`,
                position: 'sticky',
                marginTop: `calc((${theme.appShell.headerHeightMd} + ${beforeHeaderHeight}) * -1 - calc(${fabIconSize} / 2))`,
                top: `calc((${theme.appShell.headerHeightMd} + ${beforeHeaderHeight}) / 2 - (${fabIconSize} / 2))`,
              },
              [theme.breakpoints.down('md')]: {
                position: 'sticky',
                marginTop: `calc((${theme.appShell.headerHeightSm} + ${beforeHeaderHeight}) * -1 - calc(${fabIconSize} / 2))`,
                top: `calc((${theme.appShell.headerHeightSm} + ${beforeHeaderHeight}) / 2 - (${fabIconSize} / 2))`,
                padding: `0 20px`,
                '@media (max-height: 530px) and (orientation: portrait)': {
                  display: 'none',
                },
              }
            })}
          >
            {menuFab}
            {cartFab}
          </Box>
        ) : (
          <div />
        )}
        <div className={classes.children}>{children}</div>
        <div className={classes.footer}>{footer}</div>
      </LayoutProvider>
    </Box>
  )
}
